import React, { useState, useEffect } from "react";
import { X, Loader2, User } from "lucide-react";
import { createContact, updateContact } from "../../services/MailingListService";

// US States for dropdown
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
];

/**
 * ContactEditModal - Modal for creating or editing a contact
 */
const ContactEditModal = ({ listId, contact, onClose, onSaved }) => {
  const isEditing = !!contact;

  const [formData, setFormData] = useState({
    name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: ""
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Populate form when editing
  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || "",
        address_line1: contact.address_line1 || "",
        address_line2: contact.address_line2 || "",
        city: contact.city || "",
        state: contact.state || "",
        zip: contact.zip || "",
        phone: contact.phone || "",
        email: contact.email || ""
      });
    }
  }, [contact]);

  // Update form field
  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Validate form
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Please enter a contact name");
      return false;
    }
    if (!formData.address_line1.trim()) {
      setError("Please enter an address");
      return false;
    }
    if (!formData.city.trim()) {
      setError("Please enter a city");
      return false;
    }
    if (!formData.state) {
      setError("Please select a state");
      return false;
    }
    if (!formData.zip.trim()) {
      setError("Please enter a ZIP code");
      return false;
    }
    // Basic ZIP validation
    const zipPattern = /^\d{5}(-\d{4})?$/;
    if (!zipPattern.test(formData.zip.trim())) {
      setError("Please enter a valid ZIP code (e.g., 12345 or 12345-6789)");
      return false;
    }
    return true;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setIsSaving(true);
      setError(null);

      const data = {
        name: formData.name.trim(),
        address_line1: formData.address_line1.trim(),
        address_line2: formData.address_line2.trim() || undefined,
        city: formData.city.trim(),
        state: formData.state,
        zip: formData.zip.trim(),
        phone: formData.phone.trim() || undefined,
        email: formData.email.trim() || undefined
      };

      let savedContact;
      if (isEditing) {
        savedContact = await updateContact(listId, contact.id, data);
      } else {
        savedContact = await createContact(listId, data);
      }

      onSaved(savedContact);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Form field component
  const FormField = ({ label, field, required = false, type = "text", placeholder, maxLength }) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={formData[field]}
        onChange={(e) => updateField(field, e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
        disabled={isSaving}
      />
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 dark:bg-slate-950/80 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-bold dark:text-white text-slate-900">
              {isEditing ? "Edit Contact" : "Add Contact"}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-4">
          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Contact info */}
          <FormField
            label="Contact Name"
            field="name"
            required
            placeholder="John Smith"
            maxLength={100}
          />

          {/* Address section */}
          <div className="pt-2">
            <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
              Mailing Address
            </h4>

            <div className="space-y-4">
              <FormField
                label="Address Line 1"
                field="address_line1"
                required
                placeholder="123 Main Street"
                maxLength={200}
              />

              <FormField
                label="Address Line 2"
                field="address_line2"
                placeholder="Apt 4B"
                maxLength={200}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="City"
                  field="city"
                  required
                  placeholder="New York"
                  maxLength={100}
                />

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.state}
                    onChange={(e) => updateField("state", e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    disabled={isSaving}
                  >
                    <option value="">Select</option>
                    {US_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <FormField
                label="ZIP Code"
                field="zip"
                required
                placeholder="10001"
                maxLength={10}
              />
            </div>
          </div>

          {/* Optional fields */}
          <div className="pt-2">
            <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
              Optional Information
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Phone"
                field="phone"
                type="tel"
                placeholder="(555) 123-4567"
                maxLength={20}
              />

              <FormField
                label="Email"
                field="email"
                type="email"
                placeholder="john@example.com"
                maxLength={100}
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex-1 px-4 py-2.5 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-lg font-bold transition-colors shadow-lg shadow-sky-900/20 disabled:shadow-none flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : isEditing ? (
              "Save Changes"
            ) : (
              "Add Contact"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactEditModal;
